import {useEffect} from 'react';

import {VEHICLE_FORM_EVENTS} from '../../core/machine/constants/VEHICLE_FORM_EVENTS';
import {useVehicleFormContext} from './useVehicleFormContext';
import {useVehicleVariant} from './useVehicleVariant';

/**
 * Hook that synchronizes vehicle variant data with the XState machine
 * Must be called within a VehicleForm context
 *
 * @param vehicleId - The ID of the vehicle to fetch variant data for
 * @throws Error if not called within VehicleFormProvider
 *
 * @example
 * ```tsx
 * const MyFormComponent = ({vehicleId}: {vehicleId: string}) => {
 *   // This hook automatically syncs variant data with the machine
 *   useVehicleVariantSync(vehicleId);
 *
 *   // Now any component can access variant data via useVehicleVariantFromContext
 *   return <SomeFormContent />;
 * };
 * ```
 */
export function useVehicleVariantSync(vehicleId: string) {
  const {sendMachineEvent} = useVehicleFormContext();

  const [{hasSaleVehicle, hasRentalVehicle, hasServiceVehicle}, {isLoading}] =
    useVehicleVariant(vehicleId);

  useEffect(() => {
    if (!isLoading) {
      sendMachineEvent({
        type: VEHICLE_FORM_EVENTS.VEHICLE_VARIANT_UPDATE,
        vehicleVariant: {
          hasSaleVehicle: hasSaleVehicle ?? false,
          hasRentalVehicle: hasRentalVehicle ?? false,
          hasServiceVehicle: hasServiceVehicle ?? false,
        },
      });

      sendMachineEvent({
        type: VEHICLE_FORM_EVENTS.VEHICLE_ID_UPDATE,
        vehicleId,
      });
    }
  }, [hasSaleVehicle, hasRentalVehicle, hasServiceVehicle, isLoading, sendMachineEvent, vehicleId]);

  // Hook doesn't need to return anything - it just handles side effects
}
